import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import type { LeadFieldsResponse } from "@gioia365/contracts";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { useTab } from "../../api/queries";
import { AboutBox } from "../../design/components/AboutBox";
import { Button } from "../../design/components/Button";
import { Checkbox } from "../../design/components/Checkbox";
import { Chip } from "../../design/components/Chip";
import { DataTable } from "../../design/components/DataTable";
import { Dialog } from "../../design/components/Dialog";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { SaveBar } from "../../design/components/SaveBar";
import { Select } from "../../design/components/Select";
import { SettingsLayout } from "../../design/components/SettingsLayout";
import { TextField } from "../../design/components/TextField";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { useT, type Translate } from "../../i18n/locale";
import { DERIVED_PLACE_REFS } from "../../leads/card/card-fields";
import { leadFieldsQuery, usePublishLeadFields } from "../../leads/lead-queries";
import { useAccess } from "../../session/access";
import { ConfirmChangesDialog, draftSummary, LeaveDialog, useLeaveGuard, type Feedback } from "./draft-dialogs";
import {
  FIELD_TYPES,
  LIMITS,
  LOCKED_SECTION,
  PROTECTED_REFS,
  TYPE_IN_TEXT,
  TYPE_NAME,
  applyDraft,
  cleanLabel,
  fieldKeyFor,
  fieldName,
  keyOfRef,
  nameInputWidth,
  optionIdFor,
  sectionKeysOf,
  sectionName,
  type DraftEdit,
  type DraftField,
  type DraftSection,
  type DraftState,
  type FieldType,
  type SectionKey,
} from "./lead-fields-draft";
import "./lead-fields.css";

// Impostazioni → Campi della scheda (piano M2 Task 20, mock-up `regole.html?sezione=campi`): le sezioni della scheda
// del Lead (due dal 1° ottobre 2026; «Territorio» resta nei modelli pubblicati prima), ognuna con i suoi campi in tabella
// («Ordine», «Campo», «Tipo», «Voci», «Nella scheda»), e i campi
// dell'azienda con il loro tipo e le voci. Tutto in una bozza (le modifiche di `lead-fields-draft.ts`), con la barra «N
// modifiche da salvare», «Salva…» con «Cosa cambia per il team» e una sola pubblicazione. La vede ogni persona attiva;
// la cambia chi ha `settings.lead_fields`, e mai il Super admin di Gioia365 (come «Calendario, fuso e lingua», questione
// 43: sono regole dell'azienda, anche se il server lo permetterebbe). «Incasso mensile» non c'è finché la questione
// aperta 52 non è decisa. Un campo archiviato non si ripristina da qui (questione aperta 71).

export function LeadFieldsPage() {
  return (
    <SettingsLayout section="leadFields">
      <LeadFieldsSection />
    </SettingsLayout>
  );
}

function LeadFieldsSection() {
  const t = useT();
  const tab = useTab();
  const access = useAccess();
  const fields = useQuery(leadFieldsQuery(tab));
  const organization = access.context.organization.name;
  const allowed = access.can("settings.lead_fields") && !access.isPlatform;
  const editable = allowed && fields.data?.canEdit === true;

  let body: ReactNode;
  if (fields.data) {
    body = <LeadFieldsEditor base={fields.data} editable={editable} />;
  } else if (fields.isError) {
    body = (
      <div className="g-leadfields__problem">
        <Reason tone="warning" role="alert">
          {messageForError(t, fields.error)}
        </Reason>
        <Button onClick={() => void fields.refetch()}>{t("common.retry")}</Button>
      </div>
    );
  } else {
    body = <p className="g-leadfields__muted">{t("common.loading")}</p>;
  }

  return (
    <>
      {fields.data ? <p className="g-leadfields__muted">{t("leadFields.version", { version: fields.data.version })}</p> : null}
      <AboutBox title={t("leadFields.about.title")}>
        <p>{t("leadFields.about.text", { organization })}</p>
        <h2>{t("leadFields.about.whyTitle")}</h2>
        <p>{t("leadFields.about.why")}</p>
      </AboutBox>
      {!allowed || (fields.data && !fields.data.canEdit) ? (
        <Reason>{access.isPlatform ? t("leadFields.readOnlyPlatform", { organization }) : t("leadFields.readOnly", { organization })}</Reason>
      ) : null}
      {body}
    </>
  );
}

/** Il rifiuto del tipo di un campo che ha valori su qualche Lead, anche archiviato o unito (`details.valuesOnAnyLead`). */
function typeRefusal(error: unknown): { key: string; count: number } | null {
  if (!(error instanceof ApiRequestError) || error.code !== "card_model_invalid") return null;
  const key = error.details?.field;
  const count = error.details?.valuesOnAnyLead;
  return typeof key === "string" && typeof count === "number" ? { key, count } : null;
}

/** Il nome di un campo che resta fermo mentre lo si riscrive: per i nomi accessibili dei suoi controlli. */
function stableName(state: DraftState, ref: string, t: Translate): string {
  const key = keyOfRef(ref);
  const field = key === null ? undefined : state.fields.get(key);
  return field ? field.originalLabel : fieldName(state, ref, t);
}

/**
 * La riga sotto il nome di un campo di Gioia365: provincia, regione e nazione vengono dalla città e non si scrivono;
 * dei due telefoni basta uno (decisione dell'1 ottobre 2026); il secondo titolare dice di che cosa è fatto e che
 * compare sulla scheda soltanto quando si aggiunge (così anche il suo cellulare).
 */
function systemFieldSub(t: Translate, ref: string): string {
  if (DERIVED_PLACE_REFS.has(ref)) return t("leadFields.field.fromCity");
  if (ref === "system:mobile") return t("leadFields.field.mobile");
  if (ref === "system:landline") return t("leadFields.field.landline");
  if (ref === "system:second_holder") return t("leadFields.field.secondHolder");
  return t("leadFields.field.system");
}

function usedByText(t: Translate, field: DraftField): string {
  if (field.usedBy === 0) return t("leadFields.field.unused");
  return field.usedBy === 1 ? t("leadFields.field.usedByOne") : t("leadFields.field.usedBy", { count: field.usedBy });
}

/** Due rinomine di fila dello stesso campo o della stessa voce sono una sola: la bozza non cresce a ogni tasto. */
function sameTarget(previous: DraftEdit | undefined, next: DraftEdit): boolean {
  if (previous?.kind === "renameField" && next.kind === "renameField") return previous.key === next.key;
  if (previous?.kind === "renameOption" && next.kind === "renameOption") return previous.key === next.key && previous.optionId === next.optionId;
  return false;
}

/** Il controllo da rimettere a fuoco dopo una modifica (la riga si è spostata), o il suo ripiego se è spento. */
type PendingFocus = { id: string; fallback?: string };

type Editor = {
  state: DraftState;
  editable: boolean;
  disabled: boolean;
  baseId: string;
  refused: ReadonlyMap<string, number>;
  dispatch: (edit: DraftEdit, focus?: PendingFocus) => void;
};

const controlId = (baseId: string, what: string, ref: string) => `${baseId}-${what}-${ref}`;

function LeadFieldsEditor({ base, editable }: { base: LeadFieldsResponse; editable: boolean }) {
  const t = useT();
  const tab = useTab();
  const toast = useToast();
  const publish = usePublishLeadFields(tab);
  const baseId = useId();
  const [edits, setEdits] = useState<DraftEdit[]>([]);
  // La finestra «Cosa cambia per il team», con il rifiuto del server se il salvataggio si è fermato.
  const [confirming, setConfirming] = useState<{ error: Feedback | null } | null>(null);
  const [saving, setSaving] = useState(false);
  // Ciò che la pagina dice sopra la bozza: un conflitto di versione, o un tipo che il server ha rifiutato.
  const [notice, setNotice] = useState<Feedback | null>(null);
  // I campi il cui tipo il server ha rifiutato di cambiare: con i Lead archiviati o uniti i valori ci sono comunque.
  const [refused, setRefused] = useState<ReadonlyMap<string, number>>(new Map());
  const [creating, setCreating] = useState(false);
  const [pendingFocus, setPendingFocus] = useState<PendingFocus | null>(null);
  // La versione su cui la bozza è stata pensata. Il modello si rilegge anche da solo (la scheda del browser torna in primo
  // piano, la rete torna): se arriva più nuovo, la bozza ci si rimette sopra e la pagina lo dice come per un 409, così la
  // pubblicazione non parte da una versione che nessuno ha guardato (D6, questione aperta 108).
  const [draftVersion, setDraftVersion] = useState<number | null>(null);
  // Un salvataggio in corso, letto anche da un secondo clic che arriva prima del nuovo disegno.
  const savingNow = useRef(false);

  // Senza il permesso (che può sparire con un contesto riletto) si vede il pubblicato, non una bozza che non si può salvare.
  const draft = useMemo(() => applyDraft(base, editable ? edits : [], t), [base, editable, edits, t]);
  const { state, summary, problem, body } = draft;
  const dirty = editable && summary.length > 0;
  const blocker = useLeaveGuard(dirty);
  const hasEdits = editable && edits.length > 0;

  useEffect(() => {
    if (!hasEdits) {
      if (draftVersion !== null) setDraftVersion(null);
      return;
    }
    if (draftVersion === null) {
      setDraftVersion(base.version);
      return;
    }
    if (base.version === draftVersion) return;
    setDraftVersion(base.version);
    // La propria pubblicazione rilegge il modello mentre salva: quella non è un conflitto.
    if (savingNow.current) return;
    setConfirming(null);
    setNotice({ text: (tr) => tr("leadFields.conflict") });
  }, [hasEdits, base.version, draftVersion]);

  useEffect(() => {
    if (!pendingFocus) return;
    const candidates = [pendingFocus.id, pendingFocus.fallback].flatMap((id) => (id ? [document.getElementById(id)] : []));
    const target = candidates.find((element): element is HTMLElement => element instanceof HTMLElement && !element.matches(":disabled"));
    target?.focus();
    setPendingFocus(null);
  }, [pendingFocus]);

  const disabled = !editable || saving;

  function dispatch(edit: DraftEdit, focus?: PendingFocus) {
    if (!editable || savingNow.current) return;
    setEdits((current) => (sameTarget(current.at(-1), edit) ? [...current.slice(0, -1), edit] : [...current, edit]));
    if (focus) setPendingFocus(focus);
  }

  function discard() {
    setEdits([]);
    setNotice(null);
    toast.show(t("settings.draft.discarded"));
  }

  async function save() {
    if (!editable || savingNow.current || problem !== null || summary.length === 0) return;
    savingNow.current = true;
    setSaving(true);
    setConfirming({ error: null });
    try {
      // La promessa si chiude a modello già riletto: niente versione vecchia fra la risposta e la rilettura.
      await publish.mutateAsync(body);
      // La conferma si chiude prima dell'avviso (D16).
      setConfirming(null);
      setEdits([]);
      setNotice(null);
      toast.show(t("leadFields.saved"), "success");
    } catch (error) {
      const refusal = typeRefusal(error);
      if (error instanceof ApiRequestError && error.code === "version_conflict") {
        // Il modello è già stato riletto: la bozza si rimette sopra la versione nuova, da controllare (questione 108).
        setConfirming(null);
        setNotice({ text: (tr) => tr("leadFields.conflict") });
      } else if (refusal) {
        // Il tipo torna quello pubblicato e non si cambia più; il resto della bozza resta, da salvare di nuovo.
        const remaining = edits.filter((edit) => !(edit.kind === "changeType" && edit.key === refusal.key));
        const published = base.definitions.find((definition) => definition.key === refusal.key);
        const name = published?.label ?? refusal.key;
        const type = published?.type ?? "text";
        const text = (tr: Translate) =>
          tr(refusal.count === 1 ? "leadFields.errors.typeInUseOne" : "leadFields.errors.typeInUse", { field: name, type: tr(TYPE_IN_TEXT[type]), count: refusal.count });
        setEdits(remaining);
        setRefused((current) => new Map(current).set(refusal.key, refusal.count));
        if (applyDraft(base, remaining, t).summary.length === 0) {
          setConfirming(null);
          setNotice({ text });
        } else {
          setConfirming({ error: { text } });
        }
      } else {
        // La finestra resta con la bozza e dice perché: si torna alle modifiche o si riprova.
        setConfirming({ error: { text: (tr) => messageForError(tr, error) } });
      }
    } finally {
      savingNow.current = false;
      setSaving(false);
    }
  }

  function stay() {
    if (blocker.status === "blocked") blocker.reset();
  }

  function leave() {
    setEdits([]);
    if (blocker.status === "blocked") blocker.proceed();
  }

  const editor: Editor = { state, editable, disabled, baseId, refused, dispatch };
  const newFields = [...state.fields.values()].filter((field) => field.isNew).length;
  const addId = `${baseId}-add`;

  return (
    <>
      {notice ? (
        <Reason tone="warning" role="alert">
          {notice.text(t)}
        </Reason>
      ) : null}

      {state.sections.map((section, index) => (
        <SectionBlock key={section.key} editor={editor} section={section} index={index} count={state.sections.length} addId={addId} />
      ))}

      {editable ? (
        <p>
          <Button id={addId} onClick={() => setCreating(true)} disabled={disabled || newFields >= LIMITS.newFields}>
            <Icon name="plus" />
            {t("leadFields.add")}
          </Button>
        </p>
      ) : null}

      <ul className="g-leadfields__notes">
        <li>{t("leadFields.notes.type")}</li>
        <li>{t("leadFields.notes.options")}</li>
        <li>{t("leadFields.notes.hidden")}</li>
        <li>{t("leadFields.notes.phones")}</li>
      </ul>

      {state.outside.length > 0 ? <OutsideFields editor={editor} /> : null}
      {state.archived.length > 0 ? <ArchivedFields state={state} base={base} /> : null}

      {dirty ? (
        <SaveBar
          summary={draftSummary(t, summary.length)}
          problem={problem ?? undefined}
          onDiscard={discard}
          onSave={() => setConfirming({ error: null })}
          saveLabel={t("settings.draft.save")}
          saving={saving}
        />
      ) : null}

      {creating ? (
        <NewFieldDialog
          state={state}
          onClose={() => setCreating(false)}
          onCreate={(edit) => {
            setCreating(false);
            dispatch(edit, { id: controlId(baseId, "name", `field:${edit.key}`) });
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmChangesDialog changes={summary} error={confirming.error} saving={saving} onBack={() => setConfirming(null)} onApply={() => void save()} />
      ) : null}

      {blocker.status === "blocked" ? <LeaveDialog onStay={stay} onLeave={leave} /> : null}
    </>
  );
}

type SectionProps = { editor: Editor; section: DraftSection; index: number; count: number; addId: string };

function SectionBlock({ editor, section, index, count, addId }: SectionProps) {
  const t = useT();
  const { editable, disabled, baseId, dispatch } = editor;
  const name = sectionName(section.key, t);
  const headingId = `${baseId}-section-${section.key}`;
  const lockId = `${headingId}-lock`;
  const locked = section.key === LOCKED_SECTION;
  const upId = `${headingId}-up`;
  const downId = `${headingId}-down`;
  return (
    <section className="g-leadfields__section" aria-labelledby={headingId} data-hidden={section.visible ? undefined : "true"}>
      <div className="g-leadfields__section-head">
        <h2 id={headingId}>{name}</h2>
        <div className="g-leadfields__tools">
          {editable ? (
            <>
              <Button
                id={upId}
                variant="ghost"
                className="g-leadfields__icon"
                aria-label={t("leadFields.section.up", { section: name })}
                disabled={disabled || index === 0}
                onClick={() => dispatch({ kind: "moveSection", key: section.key, direction: "up" }, { id: upId, fallback: downId })}
              >
                <Icon name="up" />
              </Button>
              <Button
                id={downId}
                variant="ghost"
                className="g-leadfields__icon"
                aria-label={t("leadFields.section.down", { section: name })}
                disabled={disabled || index === count - 1}
                onClick={() => dispatch({ kind: "moveSection", key: section.key, direction: "down" }, { id: downId, fallback: upId })}
              >
                <Icon name="down" />
              </Button>
            </>
          ) : null}
          <Checkbox
            id={`${headingId}-visible`}
            label={t("leadFields.section.visible")}
            aria-label={t("leadFields.section.visibleNamed", { section: name })}
            checked={section.visible}
            disabled={disabled || locked}
            describedBy={locked ? lockId : undefined}
            onChange={(visible) => dispatch({ kind: "toggleSection", key: section.key, visible })}
          />
        </div>
      </div>
      {locked ? (
        <p id={lockId} className="g-leadfields__muted">
          {t("leadFields.section.locked")}
        </p>
      ) : null}
      <div className="g-leadfields__table">
        <DataTable caption={t("leadFields.section.caption", { section: name })} captionHidden>
          <thead>
            <tr>
              <th scope="col">{t("leadFields.table.order")}</th>
              <th scope="col">{t("leadFields.table.field")}</th>
              <th scope="col">{t("leadFields.table.type")}</th>
              <th scope="col">{t("leadFields.table.options")}</th>
              <th scope="col">{t("leadFields.table.visible")}</th>
              {editable ? (
                <th scope="col">
                  <span className="g-visually-hidden">{t("leadFields.table.more")}</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {section.fields.length === 0 ? (
              <tr>
                <td colSpan={editable ? 6 : 5} className="g-leadfields__muted">
                  {t("leadFields.section.empty")}
                </td>
              </tr>
            ) : (
              section.fields.map((entry, position) => (
                <FieldRow key={entry.ref} editor={editor} section={section} entry={entry} position={position} addId={addId} />
              ))
            )}
          </tbody>
        </DataTable>
      </div>
    </section>
  );
}

type RowProps = { editor: Editor; section: DraftSection; entry: { ref: string; visible: boolean }; position: number; addId: string };

function FieldRow({ editor, section, entry, position, addId }: RowProps) {
  const t = useT();
  const { state, editable, disabled, baseId, refused, dispatch } = editor;
  const key = keyOfRef(entry.ref);
  const field = key === null ? undefined : state.fields.get(key);
  const name = fieldName(state, entry.ref, t);
  const stable = stableName(state, entry.ref, t);
  const upId = controlId(baseId, "up", entry.ref);
  const downId = controlId(baseId, "down", entry.ref);
  const nameId = controlId(baseId, "name", entry.ref);
  const typeChangeable = field !== undefined && editable && !refused.has(field.key) && (field.isNew || field.usedBy === 0);

  let sub: string;
  if (!field) sub = systemFieldSub(t, entry.ref);
  else if (field.isNew) sub = t("leadFields.field.isNew");
  else sub = usedByText(t, field);

  return (
    <tr data-hidden={entry.visible ? undefined : "true"}>
      <td className="g-leadfields__order">
        {editable ? (
          <>
            <Button
              id={upId}
              variant="ghost"
              className="g-leadfields__icon"
              aria-label={t("leadFields.field.up", { field: stable })}
              disabled={disabled || position === 0}
              onClick={() => dispatch({ kind: "moveField", ref: entry.ref, direction: "up" }, { id: upId, fallback: downId })}
            >
              <Icon name="up" />
            </Button>
            <Button
              id={downId}
              variant="ghost"
              className="g-leadfields__icon"
              aria-label={t("leadFields.field.down", { field: stable })}
              disabled={disabled || position === section.fields.length - 1}
              onClick={() => dispatch({ kind: "moveField", ref: entry.ref, direction: "down" }, { id: downId, fallback: upId })}
            >
              <Icon name="down" />
            </Button>
          </>
        ) : (
          position + 1
        )}
      </td>
      <th scope="row">
        {field && editable ? (
          <>
            <label className="g-visually-hidden" htmlFor={nameId}>
              {t("leadFields.field.name", { field: stable })}
            </label>
            <input
              id={nameId}
              className="g-field__input g-leadfields__name"
              style={{ width: nameInputWidth(field.label) }}
              value={field.label}
              maxLength={LIMITS.label}
              autoComplete="off"
              disabled={disabled}
              onChange={(event) => dispatch({ kind: "renameField", key: field.key, label: event.target.value })}
            />
          </>
        ) : (
          <span>{name}</span>
        )}
        <span className="g-table__sub">{sub}</span>
      </th>
      <td>
        {!field ? (
          "—"
        ) : typeChangeable ? (
          <Select
            id={controlId(baseId, "type", entry.ref)}
            label={t("leadFields.field.typeLabel", { field: stable })}
            hideLabel
            className="g-leadfields__select"
            value={field.type}
            disabled={disabled}
            options={FIELD_TYPES.map((type) => ({ value: type, label: t(TYPE_NAME[type]) }))}
            onChange={(value) => {
              const type = FIELD_TYPES.find((candidate) => candidate === value);
              if (type) dispatch({ kind: "changeType", key: field.key, type });
            }}
          />
        ) : (
          <>
            <span>{t(TYPE_NAME[field.type])}</span>
            {editable && !field.isNew ? <span className="g-table__sub">{t("leadFields.field.typeLocked")}</span> : null}
          </>
        )}
      </td>
      <td className="g-leadfields__options-cell">{field?.type === "choice" ? <OptionsCell editor={editor} field={field} /> : "—"}</td>
      <td>
        {PROTECTED_REFS.has(entry.ref) ? (
          <Chip icon="lock">{t("leadFields.field.always")}</Chip>
        ) : (
          <Checkbox
            id={controlId(baseId, "visible", entry.ref)}
            className="g-leadfields__bare"
            label={t("leadFields.field.visible")}
            aria-label={t("leadFields.field.visibleNamed", { field: stable })}
            checked={entry.visible}
            disabled={disabled}
            onChange={(visible) => dispatch({ kind: "toggleField", ref: entry.ref, visible })}
          />
        )}
      </td>
      {editable ? (
        <td>
          {field ? (
            <div className="g-leadfields__more">
              <Select
                id={controlId(baseId, "section", entry.ref)}
                label={t("leadFields.field.sectionLabel", { field: stable })}
                hideLabel
                className="g-leadfields__select"
                value={section.key}
                disabled={disabled}
                options={sectionKeysOf(state).map((candidate) => ({ value: candidate, label: sectionName(candidate, t) }))}
                onChange={(value) => {
                  const target = sectionKeysOf(state).find((candidate) => candidate === value);
                  if (target) dispatch({ kind: "moveFieldToSection", ref: entry.ref, section: target }, { id: controlId(baseId, "section", entry.ref) });
                }}
              />
              <Button
                variant="ghost"
                aria-label={field.isNew ? t("leadFields.field.removeNewNamed", { field: stable }) : t("leadFields.field.archiveNamed", { field: stable })}
                disabled={disabled}
                onClick={() => dispatch({ kind: "archiveField", key: field.key }, { id: addId })}
              >
                {/* Archiviare non cancella i valori: il cestino è soltanto per un campo nuovo, che sparisce. */}
                {field.isNew ? <Icon name="trash" /> : null}
                {field.isNew ? t("leadFields.field.removeNew") : t("leadFields.field.archive")}
              </Button>
            </div>
          ) : null}
        </td>
      ) : null}
    </tr>
  );
}

function OptionsCell({ editor, field }: { editor: Editor; field: DraftField }) {
  const t = useT();
  const { editable, disabled, baseId, dispatch } = editor;
  const [newLabel, setNewLabel] = useState("");
  const addInputId = controlId(baseId, "new-option", field.ref);

  if (!editable) {
    return field.options.length === 0 ? (
      <span className="g-leadfields__muted">{t("leadFields.option.none")}</span>
    ) : (
      <ul className="g-leadfields__options">
        {field.options.map((option) => (
          <li key={option.id}>
            <span>{option.label}</span>
            {option.hidden ? <Chip>{t("leadFields.option.hidden")}</Chip> : null}
          </li>
        ))}
      </ul>
    );
  }

  const full = field.options.length >= LIMITS.options;
  function add() {
    const label = cleanLabel(newLabel);
    if (!label || full) return;
    dispatch({ kind: "addOption", key: field.key, option: { id: optionIdFor(label, field.options.map((option) => option.id)), label } }, { id: addInputId });
    setNewLabel("");
  }

  return (
    <>
      {field.options.length === 0 ? <p className="g-leadfields__muted">{t("leadFields.option.none")}</p> : null}
      <ul className="g-leadfields__options">
        {field.options.map((option) => {
          const inputId = controlId(baseId, `option-${option.id}`, field.ref);
          const params = { option: option.originalLabel, field: field.originalLabel };
          return (
            <li key={option.id}>
              <label className="g-visually-hidden" htmlFor={inputId}>
                {t("leadFields.option.name", params)}
              </label>
              <input
                id={inputId}
                className="g-field__input g-leadfields__option"
                value={option.label}
                maxLength={LIMITS.label}
                autoComplete="off"
                disabled={disabled}
                onChange={(event) => dispatch({ kind: "renameOption", key: field.key, optionId: option.id, label: event.target.value })}
              />
              {option.isNew ? (
                <Button
                  variant="ghost"
                  className="g-leadfields__icon"
                  aria-label={t("leadFields.option.remove", { option: cleanLabel(option.label) || option.originalLabel })}
                  disabled={disabled}
                  onClick={() => dispatch({ kind: "removeOption", key: field.key, optionId: option.id }, { id: addInputId })}
                >
                  <Icon name="trash" />
                </Button>
              ) : (
                <Checkbox
                  id={`${inputId}-selectable`}
                  label={t("leadFields.option.selectable")}
                  aria-label={t("leadFields.option.selectableNamed", params)}
                  checked={!option.hidden}
                  disabled={disabled}
                  onChange={(selectable) => dispatch({ kind: "hideOption", key: field.key, optionId: option.id, hidden: !selectable })}
                />
              )}
            </li>
          );
        })}
      </ul>
      <div className="g-leadfields__add-option">
        <label className="g-visually-hidden" htmlFor={addInputId}>
          {t("leadFields.option.newLabel", { field: field.originalLabel })}
        </label>
        <input
          id={addInputId}
          className="g-field__input g-leadfields__option"
          value={newLabel}
          maxLength={LIMITS.label}
          autoComplete="off"
          disabled={disabled || full}
          onChange={(event) => setNewLabel(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        />
        <Button aria-label={t("leadFields.option.addNamed", { field: field.originalLabel })} disabled={disabled || full || cleanLabel(newLabel) === ""} onClick={add}>
          <Icon name="plus" />
          {t("leadFields.option.add")}
        </Button>
      </div>
    </>
  );
}

function OutsideFields({ editor }: { editor: Editor }) {
  const t = useT();
  const { state, editable, disabled, baseId, dispatch } = editor;
  const titleId = `${baseId}-outside`;
  return (
    <section className="g-leadfields__section" aria-labelledby={titleId}>
      <h2 id={titleId}>{t("leadFields.outside.title")}</h2>
      <p className="g-leadfields__muted">{t("leadFields.outside.text")}</p>
      <ul className="g-leadfields__list">
        {state.outside.map((field) => (
          <li key={field.key}>
            <span>{cleanLabel(field.label) || field.originalLabel}</span>
            {editable ? (
              <Select
                id={controlId(baseId, "place", field.ref)}
                label={t("leadFields.outside.sectionLabel", { field: field.originalLabel })}
                hideLabel
                className="g-leadfields__select"
                value=""
                disabled={disabled}
                options={[{ value: "", label: t("leadFields.outside.choose"), disabled: true }, ...sectionKeysOf(state).map((key) => ({ value: key, label: sectionName(key, t) }))]}
                onChange={(value) => {
                  const section = sectionKeysOf(state).find((candidate) => candidate === value);
                  if (section) dispatch({ kind: "moveFieldToSection", ref: field.ref, section }, { id: controlId(baseId, "name", field.ref) });
                }}
              />
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ArchivedFields({ state, base }: { state: DraftState; base: LeadFieldsResponse }) {
  const t = useT();
  const titleId = useId();
  return (
    <section className="g-leadfields__section" aria-labelledby={titleId}>
      <h2 id={titleId}>{t("leadFields.archived.title")}</h2>
      <p className="g-leadfields__muted">{t("leadFields.archived.text")}</p>
      <ul className="g-leadfields__list">
        {state.archived.map((field) => {
          const pending = !base.definitions.some((definition) => definition.key === field.key && definition.archived);
          return (
            <li key={field.key}>
              <span>{t("leadFields.archived.item", { field: field.originalLabel, usedBy: usedByText(t, field) })}</span>
              {pending ? <Chip tone="warning">{t("leadFields.archived.pending")}</Chip> : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

type NewFieldError = { on: "name" | "options"; text: string };

/** «Nuovo campo»: nome, tipo, sezione e, per un campo a scelta, le voci; la chiave e gli id nascono dai nomi. */
function NewFieldDialog({ state, onClose, onCreate }: { state: DraftState; onClose: () => void; onCreate: (edit: Extract<DraftEdit, { kind: "createField" }>) => void }) {
  const t = useT();
  const baseId = useId();
  const [name, setName] = useState("");
  const [type, setType] = useState<FieldType>("number");
  const [section, setSection] = useState<SectionKey>("salon");
  const [options, setOptions] = useState("");
  const [error, setError] = useState<NewFieldError | null>(null);
  const optionsId = `${baseId}-options`;
  const nameRef = useRef<HTMLInputElement>(null);
  // Si scrive subito il nome: la finestra, aperta dall'effetto del suo figlio, darebbe il focus a «Chiudi».
  useEffect(() => nameRef.current?.focus(), []);

  function submit() {
    const label = cleanLabel(name);
    if (!label) return setError({ on: "name", text: t("leadFields.create.errors.name") });
    const same = (value: string) => cleanLabel(value).toLocaleLowerCase("it");
    if ([...state.fields.values()].some((field) => !field.archived && same(field.label) === same(label))) {
      return setError({ on: "name", text: t("leadFields.create.errors.nameTaken") });
    }
    const labels = type === "choice" ? options.split("\n").map(cleanLabel).filter(Boolean) : [];
    if (type === "choice") {
      if (labels.length === 0) return setError({ on: "options", text: t("leadFields.create.errors.options") });
      if (new Set(labels.map(same)).size !== labels.length) return setError({ on: "options", text: t("leadFields.create.errors.optionsRepeated") });
      if (labels.length > LIMITS.options) return setError({ on: "options", text: t("leadFields.create.errors.tooManyOptions", { max: LIMITS.options }) });
      if (labels.some((option) => option.length > LIMITS.label)) return setError({ on: "options", text: t("leadFields.create.errors.optionTooLong", { max: LIMITS.label }) });
    }
    const ids: string[] = [];
    for (const option of labels) ids.push(optionIdFor(option, ids));
    onCreate({
      kind: "createField",
      key: fieldKeyFor(label, state.fields.keys()),
      label,
      type,
      section,
      options: labels.map((option, index) => ({ id: ids[index] ?? optionIdFor(option, ids), label: option })),
    });
  }

  return (
    <Dialog
      open
      title={t("leadFields.create.title")}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("leadFields.create.cancel")}</Button>
          <Button variant="primary" onClick={submit}>
            {t("leadFields.create.submit")}
          </Button>
        </>
      }
    >
      <div className="g-leadfields__form">
        <TextField
          ref={nameRef}
          id={`${baseId}-name`}
          label={t("leadFields.create.name")}
          value={name}
          maxLength={LIMITS.label}
          autoComplete="off"
          error={error?.on === "name" ? error.text : undefined}
          onChange={(event) => {
            setName(event.target.value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") submit();
          }}
        />
        <Select
          id={`${baseId}-type`}
          label={t("leadFields.create.type")}
          hint={t("leadFields.create.typeHint")}
          value={type}
          options={FIELD_TYPES.map((candidate) => ({ value: candidate, label: t(TYPE_NAME[candidate]) }))}
          onChange={(value) => {
            const next = FIELD_TYPES.find((candidate) => candidate === value);
            if (next) setType(next);
            setError(null);
          }}
        />
        <Select
          id={`${baseId}-section`}
          label={t("leadFields.create.section")}
          value={section}
          options={sectionKeysOf(state).map((key) => ({ value: key, label: sectionName(key, t) }))}
          onChange={(value) => {
            const next = sectionKeysOf(state).find((candidate) => candidate === value);
            if (next) setSection(next);
          }}
        />
        {type === "choice" ? (
          <div className="g-field">
            <label className="g-field__label" htmlFor={optionsId}>
              {t("leadFields.create.options")}
            </label>
            <textarea
              id={optionsId}
              className="g-field__input g-leadfields__textarea"
              rows={4}
              value={options}
              aria-invalid={error?.on === "options" ? "true" : undefined}
              aria-describedby={error?.on === "options" ? `${optionsId}-hint ${optionsId}-error` : `${optionsId}-hint`}
              onChange={(event) => {
                setOptions(event.target.value);
                setError(null);
              }}
            />
            <p id={`${optionsId}-hint`} className="g-field__hint">
              {t("leadFields.create.optionsHint")}
            </p>
            {error?.on === "options" ? (
              <p id={`${optionsId}-error`} className="g-field__error" role="alert">
                {error.text}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
